<template>
  <AntRow class="management">
    <AntCol :span="24">
      <!-- <p><b>searchText:</b> {{ searchText }}</p> -->
      <AntRow
        justify="space-between"
        class="search-row"
      >
        <AntCol :span="12">
          <AntRow :gutter="16">
            <AntCol :span="20">
              <AntInput
                v-model:value="searchText"
                placeholder="Search"
                allowClear
              />
            </AntCol>

            <AntCol :span="4">
              <AntButton
                type="primary"
                htmlType="submit"
                @click="onSearch"
              >
                <template #icon>
                  <SearchIcon />
                </template>
                Search
              </AntButton>
            </AntCol>
          </AntRow>
        </AntCol>

        <AntButton
          v-if="!readOnly"
          type="primary"
          htmlType="submit"
          @click="onAdd"
        >
          <template #icon>
            <AddIcon />
          </template>
          Add
        </AntButton>
      </AntRow>

      <div v-if="isDev">
        editableData: <pre style="font-size: 10px; max-height: 300px; overflow-y: auto;">{{ JSON.stringify(editableData, null, 2) }}</pre>
        <div style="display: flex;">
          <div style="width: 50%;">
            templatesData: <pre style="font-size: 10px; max-height: 300px; overflow-y: auto;">{{ JSON.stringify(templatesData, null, 2) }}</pre>
          </div>
          <div style="width: 50%;">
            dataSource: <pre style="font-size: 10px; max-height: 300px; overflow-y: auto;">{{ JSON.stringify(dataSource, null, 2) }}</pre>
          </div>
        </div>
      </div>

      <AntTable
        :columns="columns"
        :data-source="displayedDataSource"
      >
        <template #headerCell="{ title, column }">
          <template v-if="column.tooltip">
            {{ title }}
            <AntTooltip placement="top">
              <template #title>
                <span v-html="column.tooltip.split('\n').join('<br>')"></span>
              </template>
              <QuestionIcon style="color: #1890ff;" />
            </AntTooltip>
          </template>
        </template>

        <template #bodyCell="{ text, column, record }">
          <template v-if="column.dataIndex === 'templateName'">
            <AntInput
              v-if="editableData[record.key]"
              v-model:value="editableData[record.key][column.dataIndex]"
              :placeholder="column.title"
              allowClear
              @blur="e => handleNewTemplateId(record)"
            />
            <div v-else>
              {{ text }}
            </div>
          </template>

          <template v-if="column.dataIndex === 'brandText'">
            <AntInput
              v-if="editableData[record.key]"
              v-model:value="editableData[record.key][column.dataIndex]"
              :placeholder="column.title"
              allowClear
            />
            <div v-else>
              {{ text }}
            </div>
          </template>

          <template v-if="column.dataIndex === 'swaPlatforms'">
            <AntSelect
              v-if="record.isPrimary && editableData[record.key]"
              v-model:value="editableData[record.key].swaPlatforms"
              mode="multiple"
              allowClear
              :options="swaPlatformOptions"
              :placeholder="column.title"
              :dropdownMatchSelectWidth="false"
              style="width: 100%; min-width: 180px;"
              @change="onChangeSwaPlatforms(record.key)"
            />
            <div
              v-else
              class="swa-platform-tags"
            >
              <template v-if="(text || []).length">
                <AntTag
                  v-for="id in text"
                  :key="id"
                >
                  {{ swaPlatformNameById[id] || id }}
                </AntTag>
              </template>
              <span v-else>—</span>
            </div>
          </template>

          <template v-if="column.dataIndex === 'domain'">
            <div style="display: flex; align-items: center;">
              <AntSelect
                v-if="editableData[record.key]"
                v-model:value="editableData[record.key][column.dataIndex]"
                style="margin-right: 4px;"
                :options="domainOptions"
                :dropdownMatchSelectWidth="false"
                @change="onChangeDomain(record)"
              />
              <div v-else>
                {{ text }}
              </div>
              <div style="flex-shrink: 0;">
                /api-doc/...
              </div>
            </div>
          </template>

          <template v-if="['logo', 'favicon'].includes(column.dataIndex)">
            <div v-if="editableData[record.key]">
              <AntUpload
                v-model:fileList="editableData[record.key][`${column.dataIndex}FileList`]"
                :name="column.title"
                listType="picture-card"
                class="img-upload"
                :showUploadList="{ showPreviewIcon: false }"
                :maxCount="1"
                :beforeUpload="(file) => beforeUpload(file, column.dataIndex)"
                :customRequest="(info) => onUpload(info, column.dataIndex, record)"
                @remove="(file) => onRemoveUpload(file, column.dataIndex, record.key)"
              >
                <div v-if="!editableData[record.key][`${column.dataIndex}FileList`].length">
                  <LoadingIcon v-if="editableData[record.key][`${column.dataIndex}UploadLoading`]" />
                  <AddIcon v-else />
                  <div class="ant-upload-text">
                    Upload
                  </div>
                </div>
              </AntUpload>
              <div
                v-if="!editableData[record.key][`${column.dataIndex}Url`]"
                style="margin-top: 8px; font-size: 11px;"
              >
                <a
                  href="javascript:void(0)"
                  @click="onUseDefaultImage(record.key, column.dataIndex)"
                >
                  Use default {{ column.dataIndex }}
                </a>
              </div>
            </div>
            <a
              v-else-if="record[`${column.dataIndex}Url`]"
              target="_blank"
              :href="record[`${column.dataIndex}Url`]"
            >
              <img
                class="img-upload"
                :src="record[`${column.dataIndex}Url`]"
                :alt="column.title"
              >
            </a>
            <div v-else class="no-image">
              <PictureIcon />
            </div>
          </template>

          <template v-if="column.dataIndex === 'action'">
            <div
              v-if="record.key !== 'AWC_PjERm6IKAA'"
              class="action-icons"
            >
              <span v-if="editableData[record.key]">
                <SaveIcon
                  v-if="editableData[record.key].templateName"
                  title="Save"
                  @click="onSave(record)"
                />
                <CancelIcon
                  title="Cancel"
                  @click="onCancel(record)"
                />
              </span>
              <span v-else>
                <EditIcon
                  title="Edit"
                  @click="onEdit(record.key)"
                />
                <AntPopconfirm
                  v-if="!record.isPrimary"
                  title="Sure to delete?"
                  @confirm="onDelete(record)"
                >
                  <DeleteIcon title="Delete" />
                </AntPopconfirm>
              </span>
            </div>
          </template>
        </template>
      </AntTable>
    </AntCol>
  </AntRow>
</template>

<script>
import {
  inject,
  ref,
  reactive,
  watch,
  computed,
} from 'vue'

import moment from 'moment'
import ShortUniqueId from 'short-unique-id'
import { capitalize } from 'lodash'

import {
  Row as AntRow,
  Col as AntCol,
  Tag as AntTag,
  Table as AntTable,
  Input as AntInput,
  Button as AntButton,
  Select as AntSelect,
  Upload as AntUpload,
  Tooltip as AntTooltip,
  Popconfirm as AntPopconfirm,
  message as antMessage,
} from 'ant-design-vue'

import AddIcon from '@ant-design/icons-vue/lib/icons/PlusOutlined'
import EditIcon from '@ant-design/icons-vue/lib/icons/EditOutlined'
import DeleteIcon from '@ant-design/icons-vue/lib/icons/DeleteOutlined'
import SaveIcon from '@ant-design/icons-vue/lib/icons/CheckOutlined'
import CancelIcon from '@ant-design/icons-vue/lib/icons/CloseOutlined'
import SearchIcon from '@ant-design/icons-vue/lib/icons/SearchOutlined'
import LoadingIcon from '@ant-design/icons-vue/lib/icons/LoadingOutlined'
import QuestionIcon from '@ant-design/icons-vue/lib/icons/QuestionCircleOutlined'
import PictureIcon from '@ant-design/icons-vue/lib/icons/PictureFilled'

import {
  createManagementItem,
  updateManagementItem,
  deleteManagementItem,
  uploadImage,
  deleteImages,
} from '@/api/apiDocV3'

import { PLATFORM_LIST } from '@/constants/singleWalletTesting'

export default {
  name: 'TemplateManagement',
  components: {
    AntRow,
    AntCol,
    AntTag,
    AntTable,
    AntInput,
    AntButton,
    AntSelect,
    AntUpload,
    AntTooltip,
    AntPopconfirm,
    AddIcon,
    EditIcon,
    DeleteIcon,
    SaveIcon,
    CancelIcon,
    SearchIcon,
    LoadingIcon,
    QuestionIcon,
    PictureIcon,
  },
  props: {
    readOnly: Boolean,
    templatesData: Array,
    onChangeManagementData: Function,
  },
  setup(props) {
    const isDev = inject('isDev')
    const nodeEnv = inject('nodeEnv')
    const entityType = 'template'
    const S3_URL = 'https://elasticbeanstalk-ap-northeast-1-754687034624.s3.ap-northeast-1.amazonaws.com/uploads/images/'

    const searchText = ref('')
    const templatesData = reactive(props.templatesData)

    const columns = computed(() => [
      {
        title: 'Template Name',
        dataIndex: 'templateName',
        width: '180px',
      },
      {
        title: 'Brand',
        dataIndex: 'brandText',
        width: '180px',
        tooltip: 'API 文件內 {{brand}} 替代字樣',
      },
      {
        title: 'Domain',
        dataIndex: 'domain',
      },
      ...(nodeEnv.value !== 'production' ? [{
        title: 'SWA Platforms',
        dataIndex: 'swaPlatforms',
        width: '250px',
        tooltip: 'Single Wallet Testing 支援平台',
      }] : []),
      {
        title: 'Logo',
        dataIndex: 'logo',
        align: 'center',
        width: '150px',
        tooltip: '建議寬度：300 px\n大小限制：500 KB\n檔案類型：PNG, SVG, JPG',
      },
      {
        title: 'Favicon',
        dataIndex: 'favicon',
        align: 'center',
        width: '150px',
        tooltip: '建議尺寸：48 x 48 px\n大小限制：500 KB\n檔案類型：PNG, SVG, ICO',
      },
      {
        title: 'Created Time',
        dataIndex: 'createdTime',
        width: '180px',
        customRender: ({ text }) => (text ? moment(text).format('YYYY/MM/DD HH:mm:ss') : ''),
      },
      ...(props.readOnly ? [] : [{
        title: 'Action',
        dataIndex: 'action',
        align: 'center',
        width: '100px',
      }]),
    ])

    const defaultLogoFaviconByDomain = {
      'https://allwecan.info': {
        logo: 'https://allwecan.info/img/awc_logo.png',
        favicon: 'https://allwecan.info/img/awc_favicon.ico',
      },
      'https://sanatechinfo.com': {
        logo: 'https://allwecan.info/img/sanatech_logo.png',
        favicon: 'https://allwecan.info/img/sanatech_favicon.png',
      },
      'https://in2info.cc': {
        logo: 'https://allwecan.info/img/in2_logo.png',
        favicon: 'https://allwecan.info/img/in2_favicon.ico',
      },
    }

    const swaPlatformOptions = PLATFORM_LIST.map(({ id, name }) => ({
      label: name,
      value: id,
    }))

    const swaPlatformNameById = PLATFORM_LIST.reduce((acc, { id, name }) => {
      acc[id] = name
      return acc
    }, {})

    const getFileList = ({ domain, logoUrl = '', faviconUrl = '' }) => {
      const { logo, favicon } = defaultLogoFaviconByDomain[domain]
      const logoFileName = logoUrl === logo ? 'default-logo' : logoUrl.replace(S3_URL, '')
      const faviconFileName = faviconUrl === favicon ? 'default-favicon' : faviconUrl.replace(S3_URL, '')

      return {
        logoFileList: logoFileName ? [{
          uid: logoFileName,
          name: logoFileName,
          status: 'done',
          url: logoUrl,
          thumbUrl: logoUrl,
        }] : [],
        faviconFileList: faviconFileName ? [{
          uid: faviconFileName,
          name: faviconFileName,
          status: 'done',
          url: faviconUrl,
          thumbUrl: faviconUrl,
        }] : [],
      }
    }

    const setDataSource = () => props.templatesData.map(({
      id, logoUrl, faviconUrl, domain, swaPlatforms, ...d
    }) => ({
      ...d,
      key: id,
      id,
      domain,
      logoUrl,
      faviconUrl,
      swaPlatforms: Array.isArray(swaPlatforms) ? [...swaPlatforms] : [],
      ...getFileList({ domain, logoUrl, faviconUrl }),
    })).sort((a, b) => Number(Boolean(b.isPrimary)) - Number(Boolean(a.isPrimary)))

    const dataSource = ref(setDataSource())
    const displayedDataSource = ref(dataSource.value)

    watch(() => props.templatesData, (newVal) => {
      console.log('[TemplateManagement] watch props.templatesData', newVal)
      if (newVal.length) {
        Object.assign(templatesData, newVal)
        dataSource.value = setDataSource()
        displayedDataSource.value = dataSource.value
      }
    })

    const editableData = reactive({})

    const onChangeSwaPlatforms = (key) => {
      const platforms = editableData[key]?.swaPlatforms
      if (!Array.isArray(platforms)) return
      editableData[key].swaPlatforms = [...platforms].sort((a, b) => (
        (swaPlatformNameById[a] || a).localeCompare(swaPlatformNameById[b] || b)
      ))
    }

    const editableFields = [
      'templateName',
      'brandText',
      'domain',
      'swaPlatforms',
      'logoUrl',
      'logoFileList',
      'faviconUrl',
      'faviconFileList',
    ]

    const onSearch = () => {
      console.log('onSearch', searchText.value)
      displayedDataSource.value = dataSource.value.filter((d) => {
        const matched = d.templateName.toLowerCase().includes(searchText.value.toLowerCase()) || d.brandText.toLowerCase().includes(searchText.value.toLowerCase())
        return matched
      })
    }

    const handleDeleteUploadedImages = async (toDeleteImages, onSuccess, onFail) => {
      console.log('handleDeleteUploadedImages toDeleteImages', toDeleteImages)
      if (!toDeleteImages) return

      if (toDeleteImages?.length) {
        const { data, message } = await deleteImages(toDeleteImages)
        if (data) {
          if (onSuccess) onSuccess(data)
        } else {
          antMessage.error(`Failed to delete previous uploaded images: ${message}`)
          if (onFail) onFail(message)
        }
      }
    }

    const onDelete = async (record) => {
      if (record.isPrimary) return

      const messageKey = `${Date.now()}`
      antMessage.loading('', { key: messageKey })

      const { id, templateName } = record
      console.log('onDelete', { id })

      const toDeleteImages = []
      if (record.logoUrl) toDeleteImages.push(record.logoUrl.replace(S3_URL, ''))
      if (record.faviconUrl) toDeleteImages.push(record.faviconUrl.replace(S3_URL, ''))
      await handleDeleteUploadedImages(toDeleteImages)

      const { data, message } = await deleteManagementItem({ entityType, id })

      if (data) {
        delete editableData[id]
        dataSource.value = dataSource.value.filter(d => d.id !== id)
        displayedDataSource.value = dataSource.value
        const idx = templatesData.findIndex(d => d.id === id)
        templatesData.splice(idx, 1)
        props.onChangeManagementData(entityType, templatesData)

        antMessage.success({
          key: messageKey,
          content: `Delete template [${templateName}] successfully`,
        })
      } else {
        antMessage.error({
          key: messageKey,
          duration: 5,
          content: `Failed to delete template [${templateName}]: ${message}`,
        })
      }
    }

    const onEdit = (editKey) => {
      console.log('onEdit editKey', editKey)
      const data = dataSource.value.find(d => editKey === d.key)
      editableData[editKey] = Object.keys(data).reduce((acc, key) => {
        if (editableFields.includes(key)) {
          acc[key] = Array.isArray(data[key]) ? [...data[key]] : data[key]
        }
        return acc
      }, {})
    }

    const onAdd = () => {
      const defaultDomain = 'https://allwecan.info'
      const { logo: logoUrl, favicon: faviconUrl } = defaultLogoFaviconByDomain[defaultDomain]
      const newData = {
        key: new ShortUniqueId({ length: 10 }).rnd(),
        domain: defaultDomain,
        logoUrl,
        faviconUrl,
        ...getFileList({ domain: defaultDomain, logoUrl, faviconUrl }),
        isNew: true,
      }
      editableData[newData.key] = newData
      dataSource.value.splice(0, 0, editableData[newData.key])
      displayedDataSource.value = dataSource.value
    }

    const isSameEditableValue = (a, b) => {
      if (Array.isArray(a) || Array.isArray(b)) {
        return JSON.stringify(a || []) === JSON.stringify(b || [])
      }
      if (a == null && b == null) return true
      if (a == null) return b === ''
      if (b == null) return a === ''
      return a === b
    }

    const onSave = async (record) => {
      const { key } = record
      const type = editableData[key].isNew ? 'create' : 'update'
      if (type === 'update') {
        const persistedFields = ['templateName', 'brandText', 'swaPlatforms', 'domain', 'logoUrl', 'faviconUrl']
        const unchanged = persistedFields.every(field => (
          isSameEditableValue(editableData[key][field], record[field])
        ))
        if (unchanged) {
          await handleDeleteUploadedImages(
            editableData[key].toDeleteImages,
            () => { delete editableData[key].toDeleteImages },
          )
          delete editableData[key]
          return
        }
      }

      const messageKey = Date.now()
      antMessage.loading('', { key: messageKey })

      // const fileList = [
      //   {
      //     uid: 'vc-upload-1740393627949-3',
      //     size: 356232,
      //     lastModifiedDate: '2025-01-22T15:07:48.794Z',
      //     response: {
      //       fileName: '2025-02-24-18-41-07_8.8.1.png',
      //       s3Url: 'https://elasticbeanstalk-ap-northeast-1-754687034624.s3.ap-northeast-1.amazonaws.com/uploads/images/2025-02-24-18-41-07_8.8.1.png',
      //     },
      //     name: '8.8.1.png',
      //     lastModified: 1737558468794,
      //     type: 'image/png',
      //     thumbUrl: 'https://elasticbeanstalk-ap-northeast-1-754687034624.s3.ap-northeast-1.amazonaws.com/uploads/images/2025-02-24-18-41-07_8.8.1.png',
      //     percent: 100,
      //     url: 'https://elasticbeanstalk-ap-northeast-1-754687034624.s3.ap-northeast-1.amazonaws.com/uploads/images/2025-02-24-18-41-07_8.8.1.png',
      //     originFileObj: {
      //       uid: 'vc-upload-1740393627949-3',
      //     },
      //     status: 'done',
      //   },
      // ]

      await handleDeleteUploadedImages(
        editableData[key].toDeleteImages,
        () => { delete editableData[key].toDeleteImages },
      )

      if (type === 'create') editableData[key].createdTime = moment().toISOString(true)

      const updateData = { ...editableData[key], id: key, entityType }
      delete updateData.key
      delete updateData.isNew
      delete updateData.logoFileList
      delete updateData.logoUploadLoading
      delete updateData.faviconFileList
      delete updateData.faviconUploadLoading
      // 未選平台時不寫入 swaPlatforms
      if (!Array.isArray(updateData.swaPlatforms) || !updateData.swaPlatforms.length) {
        delete updateData.swaPlatforms
      }

      let data
      let message
      if (type === 'create') {
        ({ data, message } = await createManagementItem(updateData))
      } else {
        ({ data, message } = await updateManagementItem(updateData))
      }

      const findData = dataSource.value.find(d => key === d.key)

      if (data) {
        Object.assign(findData, editableData[key])
        delete editableData[key]

        antMessage.success({
          key: messageKey,
          content: `${capitalize(type)} template [${data.templateName}] successfully`,
        })

        if (type === 'create') {
          templatesData.push(data)
        } else {
          const idx = templatesData.findIndex(d => d.id === key)
          templatesData[idx] = data
        }
        props.onChangeManagementData(entityType, templatesData)
      } else {
        antMessage.error({
          key: messageKey,
          duration: 5,
          content: `Failed to ${type} template [${record.templateName}]: ${message}`,
        })
      }
    }

    const onCancel = async (record) => {
      const { key } = record
      if (record.isNew) {
        dataSource.value.splice(0, 1)
        displayedDataSource.value = dataSource.value
      }

      Object.assign(record, getFileList(record))

      const toDeleteImages = [
        ...(editableData[key].toDeleteImages || []),
        ...editableData[key].logoFileList.map(({ response }) => response?.fileName),
        ...editableData[key].faviconFileList.map(({ response }) => response?.fileName),
        ...record.logoFileList.map(({ response }) => response?.fileName),
        ...record.faviconFileList.map(({ response }) => response?.fileName),
      ].filter(i => !!i)

      if (toDeleteImages.length) await handleDeleteUploadedImages(toDeleteImages)
      delete editableData[key]
    }

    const onChangeDomain = (record) => {
      const { key, domain: originalDomain } = record
      console.log('onChangeDomain', key, record.domain, editableData[key].domain)
      const originalDefaultLogoFavicon = defaultLogoFaviconByDomain[originalDomain]
      const newDefaultLogoFavicon = defaultLogoFaviconByDomain[editableData[key].domain]

      if (record.logoUrl === originalDefaultLogoFavicon.logo || record.isNew) {
        editableData[key].logoFileList = [{
          uid: 'default-logo',
          name: 'default-logo',
          status: 'done',
          url: newDefaultLogoFavicon.logo,
          thumbUrl: newDefaultLogoFavicon.logo,
        }]
        editableData[key].logoUrl = newDefaultLogoFavicon.logo
      }

      if (record.faviconUrl === originalDefaultLogoFavicon.favicon || record.isNew) {
        editableData[key].faviconFileList = [{
          uid: 'default-favicon',
          name: 'default-favicon',
          status: 'done',
          url: newDefaultLogoFavicon.favicon,
          thumbUrl: newDefaultLogoFavicon.favicon,
        }]
        editableData[key].faviconUrl = newDefaultLogoFavicon.favicon
      }
    }

    const onUseDefaultImage = (key, logoOrFav) => {
      const { domain } = editableData[key]
      editableData[key][`${logoOrFav}Url`] = defaultLogoFaviconByDomain[domain][logoOrFav]
      editableData[key][`${logoOrFav}FileList`] = getFileList({
        domain,
        [`${logoOrFav}Url`]: defaultLogoFaviconByDomain[domain][logoOrFav],
      })[`${logoOrFav}FileList`]
    }

    const beforeUpload = (file, logoOrFav) => {
      console.log('beforeUpload file', file)
      const fileTypeLimit = logoOrFav === 'logo' ? ['image/png', 'image/svg+xml', 'image/jpeg'] : ['image/png', 'image/svg+xml', 'image/x-icon']

      if (!fileTypeLimit.includes(file.type)) {
        antMessage.error(`File type should be ${logoOrFav === 'logo' ? 'PNG / SVG / JPG' : 'PNG / SVG / ICO'}`)
        return AntUpload.LIST_IGNORE
      }

      const fileSizeLimit = file.size / 1024 < 500
      if (!fileSizeLimit) antMessage.error('File size should not over 500 KB')

      return (fileTypeLimit && fileSizeLimit) || AntUpload.LIST_IGNORE
    }

    const onRemoveUpload = async (file, logoOrFav, key) => {
      console.log('onRemoveUpload file', file)
      editableData[key][`${logoOrFav}FileList`] = []
      editableData[key][`${logoOrFav}Url`] = ''
      if (file.uid === 'default-logo' || file.uid === 'default-favicon') return

      if (!editableData[key].toDeleteImages) editableData[key].toDeleteImages = []
      editableData[key].toDeleteImages.push(file.response?.fileName || file.name)
    }

    const onUpload = async (info, logoOrFav, record) => {
      console.log('onUpload info', info)
      const { key } = record
      editableData[key][`${logoOrFav}UploadLoading`] = true

      const { file, onSuccess, onError } = info
      const formData = new FormData()
      formData.append('image', file)

      const { data, error, message } = await uploadImage(formData)
      if (data) {
        if (editableData[key][`${logoOrFav}Url`]) {
          if (!editableData[key].toDeleteImages) editableData.toDeleteImages = []
          editableData[key].toDeleteImages.push(editableData[key][`${logoOrFav}Url`])
        }

        editableData[key][`${logoOrFav}Url`] = data.s3Url
        onSuccess(data)
      } else {
        antMessage.error(`Failed to upload image: ${message}`)
        onError(error)
      }
      editableData[key][`${logoOrFav}UploadLoading`] = false
    }

    const handleNewTemplateId = (record) => {
      console.log('handleNewTemplateId', record)
      if (record.isNew) {
        const uniqueId = record.key.split('_').pop()
        const newId = `${editableData[record.key].templateName}_${uniqueId}`
        editableData[newId] = editableData[record.key]
        delete editableData[record.key]
        record.key = newId
        record.id = record.key
      }
    }

    return {
      isDev,
      searchText,
      onSearch,
      onAdd,
      templatesData, // eslint-disable-line vue/no-dupe-keys
      dataSource,
      displayedDataSource,
      columns,
      editableData,
      domainOptions: Object.keys(defaultLogoFaviconByDomain).map(domain => ({ label: domain, value: domain })),
      swaPlatformOptions,
      swaPlatformNameById,
      onChangeSwaPlatforms,
      onUpload,
      onRemoveUpload,
      beforeUpload,
      onDelete,
      onEdit,
      onSave,
      onCancel,
      onChangeDomain,
      onUseDefaultImage,
      handleNewTemplateId,
      defaultLogoFaviconByDomain,
    }
  },
}
</script>

<style scoped>
.search-row {
  margin-bottom: 40px;
}

.swa-platform-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.swa-platform-tags :deep(.ant-tag) {
  margin-inline-end: 0;
}

.no-image {
  display: inline-block;
  width: 60px;
  height: 60px;
  background: #eee;
  color: #fff;
  font-size: 40px;
  border-radius: 5px;
}

.img-upload {
  min-width: 60px;
  max-width: 60px;
  max-height: 60px;
}

.img-upload :deep(.ant-upload.ant-upload-select-picture-card), .img-upload :deep(.ant-upload-list-picture-card-container) {
  width: 60px;
  height: 60px;
  margin: 0px;
}

.img-upload :deep(.ant-upload-select-picture-card i) {
  font-size: 32px;
  color: #999;
}

.img-upload :deep(.ant-upload-select-picture-card .ant-upload-text) {
  color: #666;
}

.img-upload :deep(.ant-upload-list-picture-card .ant-upload-list-item-info::before) {
  left: 0;
}
</style>
